import React, { useContext, useRef, useEffect } from "react";
import { Form } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../starek.css";
import { gql, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import {
    SidebarView,
    SidebarContextProvider,
    useSyncedField,
} from "../../components/Sidebar";
import Page from "../../components/Sidebar/Page";
import { longDateString } from "../../utils";

function AddPartSidebarContent({ item, plantId, onDiscard, onSave, onDelete }) {
    const starekContext = useContext(StarekContext);
    const entityIdRef = useRef(item?.id);

    // Track entity changes to prevent reinitializing form during save
    useEffect(() => {
        if (item?.id !== entityIdRef.current) {
            entityIdRef.current = item?.id;
        }
    }, [item?.id]);

    const CREATE_ONE_PART_MUTATION = gql`
        mutation CreateOnePart($data: PartCreateInput!) {
            createOnePart(data: $data) {
                name
                description
                created_at
                created_by {
                    name
                }
                updated_by {
                    name
                }
            }
        }
    `;

    const UPDATE_ONE_PART_MUTATION = gql`
        mutation UpdateOnePart(
            $data: PartUpdateInput!
            $where: PartWhereUniqueInput!
        ) {
            updateOnePart(data: $data, where: $where) {
                name
                description
                updated_at
                updated_by {
                    name
                }
            }
        }
    `;

    const DELETE_ONE_PART_MUTATION = gql`
        mutation DeleteOnePart($where: PartWhereUniqueInput!) {
            deleteOnePart(where: $where) {
                id
                deleted_at
            }
        }
    `;

    const [savePart] = useMutation(CREATE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [updatePart] = useMutation(UPDATE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });
    const [deletePart] = useMutation(DELETE_ONE_PART_MUTATION, {
        client: starekContext.engineClient,
    });

    // Synced fields
    const [name, setName] = useSyncedField("name", item?.name || "");
    const [description, setDescription] = useSyncedField(
        "description",
        item?.description || ""
    );

    return (
        <>
            <Page name="details" title="Part Details">
                <Form.Group className="mb-3" controlId="part_name">
                    <Form.Label>
                        Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Enter here"
                        name="part_name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </Form.Group>
                <Form.Group className="mb-3" controlId="part_description">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Max allowed characters: 120"
                        name="part_description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </Form.Group>
            </Page>
        </>
    );
}

function AddPartSidebar({ show, item, plantId, onDiscard, onSave, onDelete }) {
    const starekContext = useContext(StarekContext);

    const handleSaveInternal = async (emittedData) => {
        const currentUserId =
            starekContext?.currentUser?.id ||
            starekContext?.user?.id ||
            starekContext?.me?.id;

        if (!currentUserId) {
            throw new Error("No current user in context");
        }

        const payload = {
            name: emittedData.name,
            description: emittedData.description || "",
            plant_id: plantId,
        };

        await onSave(payload);
    };

    const handleDeleteInternal = async () => {
        if (!item?.id) return;
        await onDelete(item);
    };

    const handleDiscardInternal = async () => {
        await onDiscard();
    };

    const getTitle = () => {
        return item?.id ? item.name || "Edit Part" : "Add a Part";
    };

    const getMetadata = () => {
        if (!item?.id) return null;

        const updatedBy =
            item.updated_by?.name || item.created_by?.name || "Unknown";
        const updatedAt = item.updated_at || item.created_at;
        const dateStr = updatedAt ? longDateString(new Date(updatedAt)) : "";

        return (
            <div className="text-muted small mb-3">
                <i className="fa fa-user me-1"></i>
                {updatedBy}
                {dateStr && (
                    <>
                        <i className="fa fa-clock ms-3 me-1"></i>
                        {dateStr}
                    </>
                )}
            </div>
        );
    };

    return (
        <SidebarContextProvider
            entity={item}
            show={show}
            onSave={handleSaveInternal}
            onDelete={item?.id ? handleDeleteInternal : null}
            onDiscard={handleDiscardInternal}
        >
            <SidebarView title={getTitle()} getMetadata={getMetadata}>
                <AddPartSidebarContent
                    item={item}
                    plantId={plantId}
                    onDiscard={onDiscard}
                    onSave={onSave}
                    onDelete={onDelete}
                />
            </SidebarView>
        </SidebarContextProvider>
    );
}

export default AddPartSidebar;
